import { addMonths } from 'date-fns';
import { useFormik } from 'formik';
import log from 'loglevel';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Account, AccountRequestWithTrial, CreateOrUpdateAccountPlanDto } from '../../@types/Account';
import { ChangeCompanyLinkedinDialog } from '../../components/ChangeCompanyLinkedinDialog';
import { NoPermissions } from '../../components/NoPermissions';
import {
  CreditsAllocationType, PricingPlan, ROUTE_VIEW_MODE, VIEW_MODE,
} from '../../consts/enums';
import { PERMISSIONS } from '../../consts/permissions';
import { useGetAccountPlanDefinitions } from '../../hooks/query/useAccounts';
import {
  useAddCredits,
  useAddUserSeats,
  useCreateAccountPlan,
  useDeleteAccountPlan,
  useGetCredits,
  useUpdateAccount,
  useUpdateAccountPlan,
} from '../../hooks/useAccount';
import { usePageViewMode } from '../../hooks/usePageViewMode';
import STRING_KEYS from '../../language/keys';
import { getInitialState } from '../AccountsPage/formik';
import { getUpdateAccountSchema } from '../AccountsPage/formik/Schema';
import { AccountAssociatedFields, AccountEditPanelContainer } from './AccountEditPanelContainer';

const TAG = '[ACCOUNT_EDIT_PANEL]';

export const PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY = 'paf';

type AccountEditPanelProps = {
  isOpen: boolean;
  onCloseClick: (preservedAccountsFiltersBase64: string | null) => void;
  onDeleteAccount: (accountId: string) => void;
  account: Account | null;
  onUpdateCompanyCompleted: () => void;
  isLoadingAccount: boolean;
  isErrorAccount: boolean;
  accountErrorMessage: string;
};

const AccountEditPanel = ({
  isOpen,
  onCloseClick,
  account,
  onDeleteAccount,
  onUpdateCompanyCompleted,
  isLoadingAccount,
  isErrorAccount,
  accountErrorMessage,
}: AccountEditPanelProps): JSX.Element => {
  const [searchParams] = useSearchParams();

  const b64Value = searchParams.get(PRESERVED_ACCOUNTS_FILTERS_PARAM_KEY);

  const viewMode = usePageViewMode([
    {
      routeParam: ROUTE_VIEW_MODE.VIEW,
      viewMode: VIEW_MODE.READ_ONLY,
      permissions: [PERMISSIONS.ACCOUNT_VIEW],
    },
    {
      routeParam: ROUTE_VIEW_MODE.EDIT,
      viewMode: VIEW_MODE.EDITABLE,
      permissions: [PERMISSIONS.ACCOUNT_EDIT],
    },
  ]);

  const { t: translate } = useTranslation();

  const [
    isOpenDialogAssociatedCompany,
    setIsOpenDialogAssociatedCompany,
  ] = useState(false);
  const [linkedinUrlTemp, setIsLinkedinUrlTemp] = useState('');

  const { data: planDefinitions = [] } = useGetAccountPlanDefinitions();

  const updateAccount = useUpdateAccount(() => {
    // onUpdateCompanyCompleted();
  }, account?.id || '');
  const createAccountPlan = useCreateAccountPlan();
  const updateAccountPlan = useUpdateAccountPlan(() => {
    // onUpdateCompanyCompleted();
  });
  const deleteAccountPlan = useDeleteAccountPlan(account?.id || '', () => {
    // onUpdateCompanyCompleted();
  });
  const addUserSeats = useAddUserSeats(account?.id);
  const addCredits = useAddCredits(account?.id);
  const { data: { totalCredits, creditsUsed } = {} } = useGetCredits(account?.id);

  const formik = useFormik<AccountRequestWithTrial>({
    validationSchema: getUpdateAccountSchema(account),
    enableReinitialize: true,
    // validateOnMount: true,
    // need update initial value from server
    initialValues: getInitialState(account, totalCredits, creditsUsed),
    onSubmit: async (values): Promise<void> => {
      const saveObj: Partial<AccountRequestWithTrial> & {
        subDomainName: string;
        displayName: string;
        logo: string;
        accountStatus: AccountRequestWithTrial['accountStatus'];
        recruitingUsageType: AccountRequestWithTrial['recruitingUsageType'];
        outreachEnabledForTrial?: boolean;
      } = {
        subDomainName: values.subDomainName,
        displayName: values.displayName,
        logo: values.logo,
        accountStatus: values.accountStatus,
        recruitingUsageType: values.recruitingUsageType,
      };

      // One-way flag: only PATCH it on the false → true transition.
      // Trim it from the body when unchanged so we don't accidentally re-send it.
      const previousOutreachFlag = account?.outreachEnabledForTrial ?? false;
      if ((values.outreachEnabledForTrial ?? false) !== previousOutreachFlag) {
        saveObj.outreachEnabledForTrial = values.outreachEnabledForTrial;
      }

      await updateAccount.mutateAsync(saveObj);

      if (values.pricingPlan !== account?.plan?.planDefinition.pricingPlan) {
        const newPlanDefinition = planDefinitions.find(({ pricingPlan }) => pricingPlan === values.pricingPlan);

        const plan = {
          accountPlanDefinitionId: values.accountPlanDefinitionId,
          maximumOpenPositions: Number(values.maximumOpenPositions),
          billingCycle: newPlanDefinition.isPaidPlan ?
            values.billing :
            undefined,
          isCreditsBased: values.isCreditsBased === 'true',
          startDate:
            values.startDatePaidPlan && (values.pricingPlan as string) !== 'LITE' && (values.pricingPlan as string) !== 'PAY_PER_INTERVIEW' ?
              new Date(values.startDatePaidPlan).toISOString() :
              new Date().toISOString(),
          endDate:
            values.endDatePaidPlan && (values.pricingPlan as string) !== 'LITE' && (values.pricingPlan as string) !== 'PAY_PER_INTERVIEW' ?
              new Date(values.endDatePaidPlan).toISOString() :
              undefined,
        };

        await createAccountPlan.mutateAsync({ accountId: account?.id || '', plan });
      } else if (account?.plan) {
        const plan: Partial<CreateOrUpdateAccountPlanDto> = {
          accountPlanDefinitionId: values.accountPlanDefinitionId || undefined,
          maximumOpenPositions: Number(values.maximumOpenPositions),
          billingCycle: values.billing || undefined,
          isCreditsBased: values.isCreditsBased  === 'true',
          startDate: values.startDatePaidPlan ?
            new Date(values.startDatePaidPlan).toISOString() :
            undefined,
          endDate: values.endDatePaidPlan ?
            new Date(values.endDatePaidPlan).toISOString() :
            undefined,
        };

        await updateAccountPlan.mutateAsync({
          plan,
          accountId: account?.id || '',
          accountPlanId: account.plan.id,
        });
      }

      if (!!values.maxUserSeats && values.maxUserSeats > 0 && values.maxUserSeats !== account?.plan?.maxUserSeats) {
        await addUserSeats.mutateAsync({ seatsAmount: Number.parseInt(values.maxUserSeats.toString()) - account?.plan?.maxUserSeats });
      }

      if (!!values.addCredits && values.addCredits > 0) {
        const today = new Date();
        const nextMonth = new Date();
        nextMonth.setMonth(today.getMonth() + 1);

        await addCredits.mutateAsync({
          creditsAmount: Number.parseInt(values.addCredits.toString()),
          type: CreditsAllocationType.Purchase,
          expiresAt: nextMonth,
        });
      }

      if (
        !(
          createAccountPlan.isError &&
          updateAccountPlan.isError &&
          updateAccount.isError &&
          deleteAccountPlan.isError &&
          addUserSeats.isError &&
          addCredits.isError
        )
      ) {
        onCloseClick(b64Value);
      }

      onUpdateCompanyCompleted();

      log.debug(TAG, 'Account was Edited:', values);
    },
  });

  const { setFieldValue, handleSubmit } = formik;

  const accountAssociatedFields: AccountAssociatedFields = {
    displayName: {
      error: Boolean(formik.touched.displayName && formik.errors.displayName),
      helperText: formik.touched.displayName && formik.errors.displayName,
      value: formik.values.displayName,
    },
    subDomainName: {
      error: Boolean(
        formik.touched.subDomainName && formik.errors.subDomainName,
      ),
      helperText: formik.touched.subDomainName && formik.errors.subDomainName,
      value: formik.values.subDomainName,
    },
    accountStatus: {
      error: Boolean(
        formik.touched.accountStatus && formik.errors.accountStatus,
      ),
      helperText: formik.touched.accountStatus && formik.errors.accountStatus,
      value: formik.values.accountStatus,
    },
    recruitingUsageType: {
      error: Boolean(
        formik.touched.recruitingUsageType && formik.errors.recruitingUsageType,
      ),
      helperText: formik.touched.recruitingUsageType && formik.errors.recruitingUsageType,
      value: formik.values.recruitingUsageType,
    },
    maximumOpenPositions: {
      error: Boolean(
        formik.touched.maximumOpenPositions &&
          formik.errors.maximumOpenPositions,
      ),
      helperText:
        formik.touched.maximumOpenPositions &&
        formik.errors.maximumOpenPositions,
      value: `${formik.values.maximumOpenPositions}`,
    },
    maxUserSeats: {
      error: Boolean(formik.touched.maxUserSeats && formik.errors.maxUserSeats),
      helperText: formik.touched.maxUserSeats && formik.errors.maxUserSeats,
      value: `${formik.values.maxUserSeats}`,
    },
    addCredits: {
      error: Boolean(formik.touched.addCredits && formik.errors.addCredits),
      helperText: formik.touched.addCredits && formik.errors.addCredits,
      value: `${formik.values.addCredits}`,
    },
    isCreditsBased: {
      error: Boolean(formik.touched.isCreditsBased && formik.errors.isCreditsBased),
      helperText: formik.touched.isCreditsBased && formik.errors.isCreditsBased,
      value: `${formik.values.isCreditsBased.toString()}`,
    },
    creditsUsed: formik.values.creditsUsed,
    totalCredits: formik.values.totalCredits,
    userSeatsPlanLimit: formik.values.userSeatsPlanLimit,
    logo: formik.values?.logo,
    name: formik.values.companyInfo?.name,
    outreachEnabledForTrial: formik.values.outreachEnabledForTrial ?? false,
    persistedOutreachEnabledForTrial: account?.outreachEnabledForTrial ?? false,
  };

  const handleValuesChange = (name: string, value: string | boolean) => {
    setFieldValue(name, value);

    // reset paid-plan fields when not selecting specific plan
    if (name === 'pricingPlan' && typeof value === 'string') {
      const planDefinition = planDefinitions.find(({ pricingPlan }) => pricingPlan === value);
      formik.setValues((values) => {
        return {
          ...values,
          startDatePaidPlan: '',
          endDatePaidPlan: '',
          pricingPlan: value as PricingPlan,
          isCreditsBased: String(planDefinition?.isCreditsBased),
        };
      });
    }
  };

  const handleAssociatedCompanyChange = useCallback(
    (company: Company, linkedinUrl: string) => {
      formik.setValues((values: AccountRequestWithTrial) => {
        return {
          ...values,
          logo: company.logo,
          companyInfo: {
            id: company.id,
            name: company.name,
            size: 'SMALL', // todo: from server
            state: company.location?.state || '',
            country: company.location?.country || '',
            city: company.location?.city || '',
            isManual: false,
            linkedinUrl,
          },
        };
      });
    },
    [formik],
  );
  const handleChangeDateField = (name: string, date: Date | string) => {
    formik.setFieldValue(name, date);
  };

  const handleChangePaidPlanStartDate = (name: string, date: Date | string) => {
    formik.setValues((prev) => {
      return {
        ...prev,
        startDatePaidPlan: date,
        endDatePaidPlan: addMonths(new Date(date), 1),
      };
    });
  };

  const handleLinkedinValueChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsLinkedinUrlTemp(e.target.value);
    },
    [],
  );

  const handleOpenAssociatedCompanyDialog = useCallback(() => {
    setIsOpenDialogAssociatedCompany(true);
  }, []);

  const handleCloseAssociatedCompanyDialog = useCallback(() => {
    setIsOpenDialogAssociatedCompany(false);
  }, []);

  const handleCompanyReady = useCallback(
    (company: Company) => {
      handleAssociatedCompanyChange(company, linkedinUrlTemp);
      handleCloseAssociatedCompanyDialog();
    },
    [
      linkedinUrlTemp,
      handleCloseAssociatedCompanyDialog,
      handleAssociatedCompanyChange,
    ],
  );

  if (!viewMode) {
    return <NoPermissions />;
  }

  const getGeneralValidationErrors = () => {
    const errors: string[] = [];
    if (formik.errors?.companyInfo?.name && formik.touched?.companyInfo?.name) {
      errors.push(formik.errors.companyInfo.name);
    }
    if (formik.errors?.companyInfo?.size && formik.touched?.companyInfo?.size) {
      errors.push(formik.errors.companyInfo.size);
    }

    return errors;
  };

  return (
    <>
      <AccountEditPanelContainer
        isOpen={ isOpen }
        onCloseClick={ () => {
          onCloseClick(b64Value);
          updateAccount.reset();
        } }
        onValuesChange={ handleValuesChange }
        onSubmitForm={ handleSubmit }
        accountAssociatedFields={ accountAssociatedFields }
        accountId={ account?.id || '' }
        onDeleteAccount={ onDeleteAccount }
        linkedinUrl={ formik.values?.companyInfo?.linkedinUrl || '' }
        isError={ updateAccount.isError }
        errorMessage={ updateAccount.error?.response?.data.message }
        isUpdatingAccount={ updateAccount.isLoading }
        isLoadingAccount={ isLoadingAccount }
        accountErrorMessage={ accountErrorMessage }
        isErrorAccount={ isErrorAccount }
        viewMode={ viewMode }
        pricePlan={ formik.values.pricingPlan }
        billing={ formik.values.billing || '' }
        startDatePaidPlan={ {
          error: Boolean(
            formik.touched.startDatePaidPlan && formik.errors.startDatePaidPlan,
          ),
          helperText: formik.touched.startDatePaidPlan && formik.errors.startDatePaidPlan,
          value: formik.values.startDatePaidPlan,
        } }
        endDatePaidPlan={ {
          error: Boolean(
            formik.touched.endDatePaidPlan && formik.errors.endDatePaidPlan,
          ),
          helperText: formik.touched.endDatePaidPlan && formik.errors.endDatePaidPlan,
          value: formik.values.endDatePaidPlan,
        } }
        onChangeDateField={ handleChangeDateField }
        onChangePaidPlanStartDate={ handleChangePaidPlanStartDate }
        isErrorUpdateAccountPlan={ updateAccountPlan.isError }
        errorMessageUpdateAccountPlan={
          updateAccountPlan.error?.response?.data.message ||
          translate(STRING_KEYS.DEFAULT_ERROR_MESSAGE)
        }
        fieldsErrorsUpdateAccountPlan={
          updateAccountPlan.error?.response?.data.fieldsErrors
        }
        fieldsErrorsCreateAccountPlan={
          createAccountPlan.error?.response?.data.fieldsErrors
        }
        isErrorCreateAccountPlan={ createAccountPlan.isError }
        errorMessageCreateAccountPlan={
          createAccountPlan.error?.response?.data.message ||
          translate(STRING_KEYS.DEFAULT_ERROR_MESSAGE)
        }
        isUpdateAccountPlanLoading={ updateAccountPlan.isLoading }
        isCreateAccountPlanLoading={ createAccountPlan.isLoading }
        isDeleteAccountPlanError={ deleteAccountPlan.isError }
        errorMessageDeleteAccountPlan={
          deleteAccountPlan.error?.response?.data.message ||
          translate(STRING_KEYS.DEFAULT_ERROR_MESSAGE)
        }
        isDeleteAccountPlanLoading={ deleteAccountPlan.isLoading }
        onOpenAssociatedAccountDialogClick={ handleOpenAssociatedCompanyDialog }
        errorsValidation={ getGeneralValidationErrors() }
      />
      {isOpenDialogAssociatedCompany && (
        <ChangeCompanyLinkedinDialog
          onCompanyReady={ handleCompanyReady }
          onCancelClick={ handleCloseAssociatedCompanyDialog }
          isShowDialog={ isOpenDialogAssociatedCompany }
          onLinkedinValueChange={ handleLinkedinValueChanged }
          linkedinUrl={ linkedinUrlTemp }
        />
      )}
    </>
  );
};

export default AccountEditPanel;
